import React, { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import { settingsAPI } from '../lib/api';

const DEFAULT_BANNER = 'https://images.unsplash.com/photo-1601002257790-ebe0966a85ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXNvcnQlMjBpbnRlcmlvciUyMGJlaWdlJTIwYWVzdGhldGljfGVufDB8fHx8MTc3Mjk0OTc5OHww&ixlib=rb-4.1.0&q=85';

const REMEMBER_ME_KEY = 'hr_remember_credentials';

export const LoginPage = () => {
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // App settings state
  const [appSettings, setAppSettings] = useState({
    login_banner_url: DEFAULT_BANNER,
    app_name: 'Happy Retreats',
    app_tagline: 'Centralized inventory management for seamless hospitality operations across all properties.'
  });

  useEffect(() => {
    // Load saved credentials on mount
    const savedCredentials = localStorage.getItem(REMEMBER_ME_KEY);
    if (savedCredentials) {
      try {
        const { email: savedEmail, password: savedPassword } = JSON.parse(savedCredentials);
        setEmail(savedEmail || '');
        setPassword(savedPassword || '');
        setRememberMe(true);
      } catch (e) {
        localStorage.removeItem(REMEMBER_ME_KEY);
      }
    }
    
    // Fetch app settings
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.get();
        setAppSettings({
          login_banner_url: response.data.login_banner_url || DEFAULT_BANNER,
          app_name: response.data.app_name || 'Happy Retreats',
          app_tagline: response.data.app_tagline || 'Centralized inventory management for seamless hospitality operations across all properties.'
        });
      } catch (error) {
        console.log('Using default settings');
      }
    };
    fetchSettings();
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Save or clear credentials based on Remember Me checkbox
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, JSON.stringify({ email, password }));
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
    }

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-cover bg-center relative"
        style={{ 
          backgroundImage: `url(${appSettings.login_banner_url})` 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-black/20" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-4 font-['Playfair_Display']">
            {appSettings.app_name}
          </h1>
          <p className="text-lg text-white/90 max-w-md">
            {appSettings.app_tagline}
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xl font-['Playfair_Display']">H</span>
              </div>
              <div className="text-left">
                <h1 className="font-semibold text-foreground text-xl">Happy Retreats</h1>
                <p className="text-xs text-muted-foreground">Inventory System</p>
              </div>
            </div>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold text-center">Welcome back</CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to access the inventory system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                    data-testid="login-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11"
                    data-testid="login-password-input"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="remember-me" 
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked)}
                    data-testid="remember-me-checkbox"
                  />
                  <Label 
                    htmlFor="remember-me" 
                    className="text-sm font-normal cursor-pointer"
                  >
                    Remember me
                  </Label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 rounded-full" 
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col gap-4 pt-0">
              <div className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
                  Create account
                </Link>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
